import { Injectable, Injector, NgZone } from '@angular/core';
import { BehaviorSubject, Subject, take } from 'rxjs';
import { BaseNode } from '../../ui/Node/BaseNode';
import { AuthService } from '../auth-service/auth-service.service';
import { Art57Section } from '../../ui/Art57Section/Art57Section';
import { BulkOperationSourceEnum } from '../../ui/Node/MedicinalProductsNode/BulkOperationSourceEnum';

@Injectable({
  providedIn: 'root',
})
export class SectionService {
  constructor(
    private authService: AuthService,
    private injector: Injector,
    private ngZone: NgZone,
  ) {}

  private componentLoadSubject = new Subject<string>();
  componentLoad$ = this.componentLoadSubject.asObservable();

  private changeBulkUpdatedModelSubject =
    new Subject<BulkOperationSourceEnum>();

  changeBulkUpdatedModel$ = this.changeBulkUpdatedModelSubject.asObservable();

  notifyUpdatedBulkModel(source: BulkOperationSourceEnum): void {
    this.changeBulkUpdatedModelSubject.next(source);
  }

  loadSection(sectionName: string) {
    this.authService.setSelectedSection(sectionName);
    this.componentLoadSubject.next(sectionName);
  }

  //Products Reports selection node change
  private changeProductReportSelectedNodeSubject = new BehaviorSubject<BaseNode | null>(null);
  changeProductReportSelectedNode$ =  this.changeProductReportSelectedNodeSubject.asObservable();
  selectProductReportNode(node: BaseNode) {
    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      this.changeProductReportSelectedNodeSubject.next(node);
    });
  }

  //Meddra selection node change
  private changeMeddraSelectedNodeSubject = new Subject<BaseNode>();
  changeMeddraSelectedNode$ =
    this.changeMeddraSelectedNodeSubject.asObservable();
  selectMeddraNode(node: BaseNode) {
    this.changeMeddraSelectedNodeSubject.next(node);
  }

  // Medicinal Products
  private changeMedicinalProductsSelectedNodeSubject = new Subject<BaseNode>();
  changeMedicinalProductsSelectedNode$ =
    this.changeMedicinalProductsSelectedNodeSubject.asObservable();
  selectMedicinalProductsNode(node: BaseNode) {
    this.changeMedicinalProductsSelectedNodeSubject.next(node);
  }

  // WebTrader
  private changeWebTraderSelectedNodeSubject = new Subject<BaseNode>();
  changeWebTraderSelectedNode$ =
    this.changeWebTraderSelectedNodeSubject.asObservable();
  selectWebTraderNode(node: BaseNode) {
    this.changeWebTraderSelectedNodeSubject.next(node);
  }

  selectNode(node: BaseNode) {
    if (this.injector.get(Art57Section).isCreateAndSendProductSection()) {
      this.selectProductReportNode(node);
    } else if (this.injector.get(Art57Section).isMeddraSection()) {
      this.selectMeddraNode(node);
    } else if (this.injector.get(Art57Section).isWebtraderSection()) {
      this.selectWebTraderNode(node);
    } else if (this.injector.get(Art57Section).isMedicinalProductSection()) {
      this.selectMedicinalProductsNode(node);
    }
  }

  hasRole(role: string): boolean {
    return this.authService.hasAnyRole(role);
  }

  isWebTraderAllowed(): boolean {
    return this.hasRole('EVH_SECTION_WEBTRADER');
  }

  isAllowedProductReports(): boolean {
    return this.hasRole('EVH_SECTION_SEND_PRODUCTS');
  }

  isMedicinalProductsAllowed(): boolean {
    return this.hasRole('EVH_SECTION_MEDICINAL_PRODUCTS');
  }

  isMeddraAllowed(): boolean {
    return this.hasRole('EVH_SECTION_MEDDRA');
  }

  isPostAllowed(): boolean {
    return this.hasRole('EVH_SECTION_EVPOST');
  }

  isBulkExportAllowed(): boolean {
    return this.hasRole('EVH_SECTION_BULKEXPORT');
  }

  isBulkUpdateAllowed(): boolean {
    return this.hasRole('EVH_SECTION_BULKUPDATE');
  }
}
